// src/components/LlmProviderModelModal.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Modal from 'react-modal';
import Select from 'react-select';
import { useData } from '../context/DataContext';
import { IDIOM_OPTIONS } from "../config/MenuIdiomItems"; // For adding idioms
import styles from './LlmProviderModelModal.module.css'; // Create this CSS file
import Spinner from './Spinner';
import { FaTrash, FaEdit, FaPlus } from 'react-icons/fa'; // Icons

// react-select styles
// (Adapted for consistency with previous examples, adjust colors/sizes as needed)
const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        backgroundColor: '#3a3a3a', // Dark input background
        borderColor: state.isFocused ? '#6f9aee' : '#555', // Focus border color or default dark border
        boxShadow: state.isFocused ? '0 0 0 1px #6f9aee' : 'none', // Subtle focus shadow
        '&:hover': {
            borderColor: state.isFocused ? '#6f9aee' : '#777' // Slightly lighter border on hover (unless focused)
        },
        minHeight: '38px', // Standard height
        height: '38px',
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        opacity: state.isDisabled ? 0.7 : 1,
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: '38px',
        padding: '0 8px' // Adjust padding
    }),
    input: (provided) => ({
        ...provided,
        margin: '0px',
        padding: '0px',
        color: '#e0e0e0', // Light text color for input
    }),
    indicatorSeparator: () => ({
        display: 'none', // Hide the vertical line separator
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: '38px',
    }),
    dropdownIndicator: (provided, state) => ({
        ...provided,
        padding: '8px',
        color: state.isDisabled ? '#666' : '#aaa', // Dimmer arrow when disabled
        '&:hover': {
            color: state.isDisabled ? '#666' : '#ccc', // Lighter arrow on hover
        },
    }),
    clearIndicator: (provided) => ({
        ...provided,
        padding: '8px',
        color: '#aaa',
        '&:hover': {
            color: '#f44336', // Example: Red hover for clear button
        },
    }),
    menu: (provided) => ({
        ...provided,
        backgroundColor: '#2d2d2d', // Dark background for the dropdown menu
        borderRadius: '4px',
        border: '1px solid #555',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        zIndex: 20, // Ensure menu is above modal content
    }),
    menuList: (provided) => ({
        ...provided,
        paddingTop: '4px',
        paddingBottom: '4px',
        maxHeight: '250px', // Set a max height for scrollable menu
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? '#0056b3' : state.isFocused ? '#3a3a3a' : '#2d2d2d', // Blue selected, dark focus, default dark
        color: state.isSelected ? '#ffffff' : '#e0e0e0', // White text selected, light text otherwise
        cursor: 'pointer',
        padding: '8px 12px',
        fontSize: '0.95em',
        '&:active': { // Keep active style subtle but noticeable
            backgroundColor: '#4a4a4a',
        },
    }),
    multiValue: (provided) => ({
        ...provided,
        backgroundColor: '#4a4a4a', // Background for multi-select tags
        borderRadius: '3px',
    }),
    multiValueLabel: (provided) => ({
        ...provided,
        color: '#e0e0e0', // Text color for multi-select tags
        padding: '3px 6px',
        fontSize: '0.9em',
    }),
    multiValueRemove: (provided) => ({
        ...provided,
        color: '#aaa',
        borderRadius: '0 3px 3px 0',
        '&:hover': {
            backgroundColor: '#f44336', // Red background on remove hover
            color: 'white',
        },
    }),
    singleValue: (provided, state) => ({
        ...provided,
        color: state.isDisabled ? '#888' : '#e0e0e0', // Dimmer text when disabled, light text otherwise
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#aaa', // Placeholder text color
    }),
    noOptionsMessage: (provided) => ({
        ...provided,
        color: '#aaa',
        textAlign: 'center',
        padding: '8px 12px',
    }),
};

const IDIOM_SELECT_OPTIONS = IDIOM_OPTIONS.map(i => ({ value: i, label: i }));

const LlmProviderModelModal = () => {
    const {
        isLlmAdminModalOpen, setIsLlmAdminModalOpen,
        adminLlmConfigsList, // Full list for admin [{ llmConfigId, providerName, ... }]
        fetchAdminLlmConfigurations, // Fetch the list
        addLlmConfiguration,    // Add new entry/entries
        updateLlmConfiguration, // Update existing entry (isAvailable, maybe name)
        deleteLlmConfiguration, // Delete existing entry
        llmAdminModalLoading, llmAdminModalError, setLlmAdminModalError // Admin modal states
    } = useData();

    // --- Local State ---
    const [providerFilter, setProviderFilter] = useState(null); // {value, label}
    const [modelFilter, setModelFilter] = useState(''); // string for text filter
    const [idiomFilter, setIdiomFilter] = useState(null); // {value, label}
    const [showAddForm, setShowAddForm] = useState(false);

    // State for Add/Edit Form
    const [editMode, setEditMode] = useState(false); // true if editing existing
    const [currentConfigId, setCurrentConfigId] = useState(null); // ID being edited
    const [newProviderName, setNewProviderName] = useState('');
    const [newModelName, setNewModelName] = useState('');
    const [newIdioms, setNewIdioms] = useState([]); // For multi-select add [{value, label}]
    const [editIdiom, setEditIdiom] = useState(null); // For single idiom edit {value, label}
    const [newIsAvailable, setNewIsAvailable] = useState(true);

    // --- Effects ---
    // Fetch full list when modal opens
    useEffect(() => {
        if (isLlmAdminModalOpen) {
            setLlmAdminModalError(null); // Clear error
            fetchAdminLlmConfigurations(); // Fetch the list for the table
        } else {
            // Reset state on close
            setProviderFilter(null); setModelFilter(''); setIdiomFilter(null);
            setShowAddForm(false); resetAddEditForm();
        }
    }, [isLlmAdminModalOpen, fetchAdminLlmConfigurations, setLlmAdminModalError]);

    // --- Derived State ---
    const providerOptions = useMemo(() => {
        const providers = [...new Set((adminLlmConfigsList || []).map(c => c.providerName))];
        return providers.sort().map(p => ({ value: p, label: p }));
    }, [adminLlmConfigsList]);

    const idiomOptions = useMemo(() => { // Use static list for filtering/adding
        return IDIOM_SELECT_OPTIONS;
    }, []);

    const filteredConfigs = useMemo(() => {
        return (adminLlmConfigsList || []).filter(config => {
            if (providerFilter && config.providerName !== providerFilter.value) return false;
            if (modelFilter && !config.modelName.toLowerCase().includes(modelFilter.toLowerCase())) return false;
            if (idiomFilter && config.idiom !== idiomFilter.value) return false;
            return true;
        });
    }, [adminLlmConfigsList, providerFilter, modelFilter, idiomFilter]);

    // --- Utility Functions ---
    const resetAddEditForm = () => {
        setEditMode(false); setCurrentConfigId(null);
        setNewProviderName(''); setNewModelName(''); setNewIdioms([]);
        setEditIdiom(null); setNewIsAvailable(true);
        setLlmAdminModalError(null); // Clear form-specific errors
    };

    // --- Handlers ---
    const handleCloseRequest = useCallback(() => {
        if (llmAdminModalLoading) return;
        setIsLlmAdminModalOpen(false);
    }, [llmAdminModalLoading, setIsLlmAdminModalOpen]);

    const handleAddNewClick = () => {
        resetAddEditForm();
        setShowAddForm(true);
    };

    const handleEditClick = (config) => {
        resetAddEditForm();
        setEditMode(true);
        setCurrentConfigId(config.llmConfigId);
        setNewProviderName(config.providerName); // Pre-fill for editing
        setNewModelName(config.modelName);
        setEditIdiom({ value: config.idiom, label: config.idiom }); // Single idiom for edit
        setNewIsAvailable(config.isAvailable);
        setShowAddForm(true); // Show the form for editing
    };

    const handleCancelAddEdit = () => {
        setShowAddForm(false);
        resetAddEditForm();
    };

    const handleSave = async () => {
        setLlmAdminModalError(null); // Clear previous errors

        if (editMode) { // --- UPDATE existing ---
            if (!currentConfigId || !newProviderName.trim() || !newModelName.trim() || !editIdiom) {
                 setLlmAdminModalError("Provider, Model, and Idiom are required for editing."); return;
            }
            const updateData = {
                providerName: newProviderName.trim(),
                modelName: newModelName.trim(),
                idiom: editIdiom.value,
                isAvailable: newIsAvailable,
            };
            console.log(`[AdminModal] Updating ID ${currentConfigId} with:`, updateData);
            const result = await updateLlmConfiguration(currentConfigId, updateData);
            if (result?.status) {
                 setShowAddForm(false); resetAddEditForm(); // Close form on success
            } // Error handled by context state

        } else { // --- ADD new ---
            if (!newProviderName.trim() || !newModelName.trim() || newIdioms.length === 0) {
                 setLlmAdminModalError("Provider, Model, and at least one Idiom are required."); return;
            }
            const idiomValues = newIdioms.map(i => i.value);
            console.log(`[AdminModal] Adding: ${newProviderName}/${newModelName} for idioms:`, idiomValues);
            const result = await addLlmConfiguration(newProviderName.trim(), newModelName.trim(), idiomValues, newIsAvailable);
            if (result?.statusCode === 201 || result?.statusCode === 207) { // Handle full or partial success
                 fetchAdminLlmConfigurations(); // Fetch the list for the table
                 setShowAddForm(false); resetAddEditForm(); // Close form
                 if (result.errors && result.errors.length > 0) {
                      setLlmAdminModalError(`Partial success. Errors: ${result.errors.join(', ')}`); // Show errors if partial
                 }
            } // Error handled by context state
        }
    };

    const handleDelete = async (configId) => {
        if (llmAdminModalLoading) return;
        if (window.confirm(`Are you sure you want to delete LLM Configuration ID ${configId}? This might affect user settings.`)) {
             setLlmAdminModalError(null);
             await deleteLlmConfiguration(configId);
             // Error handled by context state
        }
    };

    // --- JSX ---
    return (
        <Modal
            isOpen={isLlmAdminModalOpen}
            onRequestClose={handleCloseRequest}
            className={styles.modalContent} // Use specific CSS module
            overlayClassName={styles.modalOverlay}
            contentLabel="Admin: Manage LLM Configurations"
            shouldCloseOnOverlayClick={!llmAdminModalLoading}
            shouldCloseOnEsc={!llmAdminModalLoading}
            aria={{ modal: 'true' }}
        >
            <h2>Admin: Manage LLM Configurations</h2>
            {llmAdminModalLoading && <Spinner message="Processing..." />}
            {llmAdminModalError && <div className={styles.modalError}>Error: {llmAdminModalError}</div>}

            {/* Add/Edit Form Area */}
            {showAddForm ? (
                <div className={styles.addEditForm}>
                    <h4>{editMode ? 'Edit Configuration' : 'Add New Configuration(s)'}</h4>
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label htmlFor="providerName">Provider Name:</label>
                            <input type="text" id="providerName" value={newProviderName} onChange={(e) => setNewProviderName(e.target.value)} disabled={llmAdminModalLoading} />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="modelName">Model Name:</label>
                            <input type="text" id="modelName" value={newModelName} onChange={(e) => setNewModelName(e.target.value)} disabled={llmAdminModalLoading} />
                        </div>
                    </div>
                    <div className={styles.formRow}>
                        <div className={styles.formGroup} style={{ flexGrow: 2 }}>
                            <label htmlFor="idioms">{editMode ? 'Idiom:' : 'Idioms:'}</label>
                            {editMode ? (
                                <Select inputId="idioms" options={idiomOptions} value={editIdiom} onChange={setEditIdiom} styles={customSelectStyles} isDisabled={true} /> // Idiom usually not editable directly per row? Or allow it? Let's make it editable.
                                // <Select inputId="idioms" options={idiomOptions} value={editIdiom} onChange={setEditIdiom} styles={customSelectStyles} isDisabled={llmAdminModalLoading} /> // Allow edit
                            ) : (
                                <Select inputId="idioms" options={idiomOptions} value={newIdioms} onChange={setNewIdioms} styles={customSelectStyles} isDisabled={llmAdminModalLoading} isMulti closeMenuOnSelect={false} placeholder="Select Idioms..." />
                            )}
                        </div>
                        <div className={styles.formGroup} style={{ justifyContent: 'center' }}>
                             <label className={styles.checkboxLabel}>
                                <input type="checkbox" checked={newIsAvailable} onChange={(e) => setNewIsAvailable(e.target.checked)} disabled={llmAdminModalLoading} />
                                Is Available?
                             </label>
                        </div>
                    </div>
                    <div className={styles.formActions}>
                        <button onClick={handleSave} className={`${styles.actionButton} ${styles.saveButton}`} disabled={llmAdminModalLoading}>{editMode ? 'Save Changes' : 'Add Configuration(s)'}</button>
                        <button onClick={handleCancelAddEdit} className={`${styles.actionButton} ${styles.closeButton}`} disabled={llmAdminModalLoading}>Cancel</button>
                    </div>
                </div>
            ) : (
                <div className={styles.tableActions}>
                    <button onClick={handleAddNewClick} className={`${styles.actionButton} ${styles.addButton}`} disabled={llmAdminModalLoading}><FaPlus /> Add New LLM Config</button>
                </div>
            )}

            {/* Filtering Controls */}
            <div className={styles.filterControls}>
                <div className={styles.filterItem}> 
                    <label>Provider:</label> 
                    <Select 
                        options={providerOptions} 
                        value={providerFilter} 
                        onChange={setProviderFilter}
                        isDisabled={showAddForm} 
                        styles={customSelectStyles} 
                        isClearable 
                        placeholder="All Providers" 
                    /> 
                </div>
                <div className={styles.filterItem}>
                    <label>Model:</label> 
                    <input type="text" 
                        value={modelFilter} 
                        onChange={(e) => setModelFilter(e.target.value)}
                        disabled={showAddForm} 
                        placeholder="Filter by model name..." 
                    /> 
                </div>
                <div className={styles.filterItem}> 
                    <label>Idiom:</label> 
                    <Select 
                        options={idiomOptions} 
                        value={idiomFilter} 
                        onChange={setIdiomFilter} 
                        isDisabled={showAddForm} 
                        styles={customSelectStyles} 
                        isClearable 
                        placeholder="All Idioms" 
                    /> 
                </div>
            </div>

            {/* Configuration Table */}
            <div className={styles.configTableContainer}>
                <table className={styles.configTable}>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Provider</th>
                            <th>Model</th>
                            <th>Idiom</th>
                            <th>Available</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredConfigs.length === 0 ? (
                            <tr><td colSpan="6">No configurations match filters or none exist.</td></tr>
                        ) : (
                            filteredConfigs.map(config => (
                                <tr key={config.llmConfigId}>
                                    <td>{config.llmConfigId}</td>
                                    <td>{config.providerName}</td>
                                    <td>{config.modelName}</td>
                                    <td>{config.idiom}</td>
                                    <td>{config.isAvailable ? 'Yes' : 'No'}</td>
                                    <td>
                                        <button onClick={() => handleEditClick(config)} className={styles.editButton} title="Edit" disabled={llmAdminModalLoading}><FaEdit /></button>
                                        <button onClick={() => handleDelete(config.llmConfigId)} className={styles.deleteButton} title="Delete" disabled={llmAdminModalLoading}><FaTrash /></button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Close Button */}
            <div className={styles.modalActions}>
                <button onClick={handleCloseRequest} className={`${styles.actionButton} ${styles.closeButton}`} disabled={llmAdminModalLoading}>Close</button>
            </div>
        </Modal>
    );
};

export default LlmProviderModelModal;